'use client';
import classNames from 'classnames';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { ButtonFragmentFragment, hasCldImage, ImageWrapperFragmentFragment } from '@fm-nx/contentful-types';
import { CldImage } from 'next-cloudinary';
import { StickyButton } from '../sticky-button/sticky-button';
import { Button } from '../button/button';
import { Breadcrumb } from '../breadcrumb/breadcrumb';
import { TableOfContents } from '../table-of-contents/table-of-contents';

type PageIntroLevel2DisplayProps = {
    entryId?: string;
    overline?: string | null;
    title?: string | null;
    headerText?: string | null;
    headerParagraph?: string | null;
    button?: ButtonFragmentFragment | null;
    image?: ImageWrapperFragmentFragment | null;
    tableOfContents?: { label: string; anchor: string }[];
    backgroundColor?: 'green-100' | 'banana' | 'powder' | 'teal-100' | 'blue-100';
    // Set by the caller when the block immediately following this hero will
    // provide its own curve (e.g. BlockImageWithPartialOverlay tucking under),
    // so the hero's curved panel doesn't stack a second curve. Callers with a
    // TOC should not set this — the TOC lives in the panel.
    hideCurvedPanel?: boolean;
};

const bgColorClass = (color?: string) =>
    classNames({
        'bg-banana': color === 'banana',
        'bg-green-100': color === 'green-100',
        'bg-powder': color === 'powder',
        'bg-teal-100': color === 'teal-100',
        'bg-blue-100': color === 'blue-100',
    });

// page intro text with optional split image layout
export const PageIntroLevel2Display = (props: PageIntroLevel2DisplayProps) => {
    const { headerText, title, headerParagraph, button, entryId, tableOfContents, hideCurvedPanel, image } = props;
    const inspectorProps = useContentfulInspectorMode({ entryId });
    const tocItems = tableOfContents ?? [];
    const hasToc = tocItems.length > 0;
    const imageCloudinary = hasCldImage(image) ? image?.cloudinaryMedia[0] : null;
    const hasImage = !!imageCloudinary;

    return (
        <>
            <div
                className={classNames(
                    bgColorClass(props.backgroundColor),
                    hasImage
                        ? 'relative overflow-clip rounded-b-(--radius-fluid-lg) w-full mx-auto max-w-container'
                        : 'pt-32 md:pt-40 lg:pt-[161px] pb-24'
                )}
            >
                {hasImage && imageCloudinary && (
                    <div className="relative mt-[97px] md:mt-0 md:absolute aspect-[3/2] md:aspect-auto md:top-[113px] md:right-0 md:bottom-0 md:w-1/2">
                        <CldImage
                            className="object-cover"
                            fill
                            src={imageCloudinary.public_id}
                            alt={image?.altText || image?.title || ''}
                            sizes="(max-width: 768px) 100vw, 50vw"
                            priority
                            format="auto"
                            quality="auto"
                            dpr="auto"
                            fetchPriority="high"
                            loading="eager"
                        />
                    </div>
                )}
                <div className={hasImage ? 'relative' : 'relative w-full mx-auto max-w-container'}>
                    <div
                        className={classNames(
                            'mx-6 md:mx-0 md:grid md:grid-cols-14 md:gap-6',
                            hasImage && 'pt-8 md:pt-40 lg:pt-[161px] pb-12 md:pb-24'
                        )}
                    >
                        <div
                            className={classNames(
                                'space-y-6',
                                hasImage ? 'md:col-span-5 md:col-start-2' : 'md:col-span-9 md:col-start-2'
                            )}
                        >
                            <Breadcrumb title={title} />
                            <h2 {...inspectorProps({ fieldId: 'title' })} className="h-xxl leading-[120%]">
                                {headerText}
                            </h2>
                            {hasImage && headerParagraph && (
                                <p
                                    {...inspectorProps({ fieldId: 'summary' })}
                                    className="leading-8 text-[24px] p-xl mt-2"
                                >
                                    {headerParagraph}
                                </p>
                            )}
                        </div>
                        {!hasImage && headerParagraph && (
                            <p
                                {...inspectorProps({ fieldId: 'summary' })}
                                className="leading-8 text-[24px] p-xl md:col-span-7 md:col-start-2 mt-2"
                            >
                                {headerParagraph}
                            </p>
                        )}
                        <div className="md:hidden">
                            {button && (
                                <div className="mt-4">
                                    <Button button={button} />
                                </div>
                            )}
                        </div>
                        {button && (
                            <div
                                className={classNames(
                                    'hidden md:block absolute z-40 right-8 md:right-16',
                                    hasImage ? 'bottom-0' : '-bottom-44 md:bottom-0'
                                )}
                            >
                                <StickyButton button={button} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {!hideCurvedPanel && (
                <div className="rounded-t-(--radius-fluid-lg) bg-white -mt-(--radius-fluid-lg) min-h-(--radius-fluid-lg)">
                    {hasToc && <TableOfContents items={tocItems} />}
                </div>
            )}
        </>
    );
};

export default PageIntroLevel2Display;
